import React from 'react';
import { Box, Container, Typography, Link, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CONTACT, PRODUCT_URLS } from '../constants';

const Footer: React.FC = () => {
  const navigate = useNavigate();

  const columns = [
    {
      title: 'Products',
      links: [
        { label: 'AdamHealth App', href: PRODUCT_URLS.adamApp, external: true },
        { label: 'iPOS', href: PRODUCT_URLS.ipos, external: true },
        { label: 'Overview', path: '/products' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About', path: '/about' },
        { label: 'Shop', path: '/shop' },
        { label: 'Contact', href: `mailto:${CONTACT.email}` },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        borderTop: '1px solid rgba(15,23,42,0.08)',
        bgcolor: '#f8fafc',
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1fr' },
            gap: { xs: 4, md: 6 },
            mb: 6,
          }}
        >
          <Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Box
                component="img"
                src="/images/zendilogo.png"
                alt="Zendi"
                sx={{ height: 26, width: 'auto' }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
              <Typography sx={{ fontWeight: 700, letterSpacing: '-0.04em', fontSize: 18 }}>
                Zendi
              </Typography>
            </Box>
            <Typography sx={{ color: 'text.secondary', maxWidth: 360, fontSize: 14 }}>
              AI-powered technology for real industries. Healthcare is where we are deepest right
              now — AdamCore, AdamHealth App, and iPOS.
            </Typography>
          </Box>

          {columns.map((col) => (
            <Box key={col.title}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  mb: 2,
                }}
              >
                {col.title}
              </Typography>
              <Stack spacing={1.25}>
                {col.links.map((link) => (
                  <Link
                    key={link.label}
                    component="button"
                    underline="none"
                    onClick={() => {
                      if ('path' in link && link.path) navigate(link.path);
                      else if ('href' in link && link.href) {
                        if ('external' in link && link.external) {
                          window.open(link.href, '_blank', 'noopener,noreferrer');
                        } else {
                          window.location.href = link.href;
                        }
                      }
                    }}
                    sx={{
                      color: 'text.secondary',
                      fontSize: 14,
                      textAlign: 'left',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            pt: 3,
            borderTop: '1px solid rgba(15,23,42,0.08)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            © {new Date().getFullYear()} Zendi Technologies
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            {CONTACT.email} · {CONTACT.phone}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
